import { useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Button, Icon } from '../components/ui';
import { useAuth } from '../lib/auth';
import { BrandProvider, useBrand } from '../lib/brand';
import { tr } from '../lib/i18n';
import { LanguageSelect } from '../components/LanguageSelect';
import { ThemeToggle } from '../components/ThemeToggle';

const NAV = [
  { to: '/busca', label: 'Encontrar profissional' },
  { to: '/precos', label: 'Planos e preços' },
  { to: '/sobre', label: 'Sobre' },
  { to: '/faq', label: 'Dúvidas frequentes' },
  { to: '/contato', label: 'Contato' }
];

export function PublicLayout({ children }: { children: ReactNode }): JSX.Element {
  return (
    <BrandProvider>
      <PublicLayoutShell>{children}</PublicLayoutShell>
    </BrandProvider>
  );
}

function PublicLayoutShell({ children }: { children: ReactNode }): JSX.Element {
  const { brand } = useBrand();
  const { user, isAdmin, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // A porta da empresa não expõe atalho para o painel administrativo.
  const showAreaLink = Boolean(user) && !(isAdmin && pathname === '/entrar');

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 border-b border-line bg-[color:var(--nl-header-surface)] backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-content items-center gap-4 px-4">
          {brand?.logoPath ? (
            <Link to="/" className="flex min-w-0 shrink-0 items-center" aria-label={tr('{brand} — página inicial', { brand: brand.name })}>
              <img
                src={brand.logoPath}
                alt={tr('Logo de {brand}', { brand: brand.name })}
                className="h-10 w-auto max-w-[160px] object-contain"
              />
            </Link>
          ) : (
            <Link to="/" className="shrink-0" aria-label={tr("AgendaPro — página inicial")}>
              <Logo size={40} />
            </Link>
          )}
          <nav aria-label={tr("Principal")} className="hidden items-center gap-1 md:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'bg-surface text-graphite' : 'text-muted hover:bg-surface hover:text-graphite'}`
                }
              >
                {tr(item.label)}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-1">
            <ThemeToggle />
            <div className="hidden items-center gap-2 md:flex">
              {user ? (
                <>
                  {showAreaLink ? (
                    <Button variant="secondary" onClick={() => navigate(isAdmin ? '/admin' : '/painel')}>
                      {isAdmin ? tr('Administração') : tr('Meu painel')}
                    </Button>
                  ) : null}
                  <Button variant="ghost" onClick={() => void logout().then(() => navigate('/'))}>{tr("Sair")}</Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate('/entrar')}>{tr("Entrar")}</Button>
                  <Button onClick={() => navigate('/comecar')}>{tr("Começar teste grátis")}</Button>
                </>
              )}
            </div>
            <button
              type="button"
              className="rounded-md p-2 hover:bg-surface md:hidden"
              aria-expanded={open}
              aria-label={tr("Abrir menu")}
              onClick={() => setOpen((v) => !v)}
            >
              <Icon name={open ? 'close' : 'menu'} size={22} />
            </button>
          </div>
        </div>
        {open ? (
          <div className="border-t border-line bg-card px-4 py-3 md:hidden">
            <nav aria-label={tr("Menu móvel")} className="flex flex-col gap-1">
              {NAV.map((item) => (
                <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-sm font-medium hover:bg-surface">
                  {tr(item.label)}
                </NavLink>
              ))}
              <div className="mt-2 flex gap-2">
                {user ? (
                  <>
                    {showAreaLink ? (
                      <Button variant="secondary" className="flex-1" onClick={() => navigate(isAdmin ? '/admin' : '/painel')}>
                        {isAdmin ? tr('Administração') : tr('Meu painel')}
                      </Button>
                    ) : null}
                    <Button variant="ghost" onClick={() => void logout().then(() => navigate('/'))}>{tr("Sair")}</Button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" className="flex-1" onClick={() => navigate('/entrar')}>{tr("Entrar")}</Button>
                    <Button className="flex-1" onClick={() => navigate('/comecar')}>{tr("Teste grátis")}</Button>
                  </>
                )}
              </div>
            </nav>
          </div>
        ) : null}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-16 border-t border-line bg-card">
        <div className="mx-auto grid w-full max-w-content gap-8 px-4 py-10 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Logo size={36} />
            <p className="nl-hint mt-3">{tr("Seu negócio. Sua agenda. Seu tempo.")}</p>
            <div className="mt-3"><LanguageSelect /></div>
          </div>
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">{tr("Produto")}</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link className="hover:underline" to="/precos">{tr("Planos e preços")}</Link></li>
              <li><Link className="hover:underline" to="/busca">{tr("Encontrar profissional")}</Link></li>
              <li><Link className="hover:underline" to="/faq">{tr("Dúvidas frequentes")}</Link></li>
              <li><Link className="hover:underline" to="/suporte">{tr("Suporte")}</Link></li>
            </ul>
          </div>
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">{tr("Institucional")}</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link className="hover:underline" to="/sobre">{tr("Sobre a AgendaPro")}</Link></li>
              <li><Link className="hover:underline" to="/contato">{tr("Contato")}</Link></li>
            </ul>
          </div>
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">{tr("Legal")}</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link className="hover:underline" to="/termos">{tr("Termos de uso")}</Link></li>
              <li><Link className="hover:underline" to="/privacidade">{tr("Política de privacidade")}</Link></li>
              <li><Link className="hover:underline" to="/cancelamento">{tr("Política de cancelamento")}</Link></li>
              <li><Link className="hover:underline" to="/cookies">{tr("Política de cookies")}</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-line px-4 py-4 text-center text-xs text-muted">
          © {new Date().getFullYear()} {tr("AgendaPro. Todos os direitos reservados.")}
        </div>
      </footer>
    </div>
  );
}
